"use client";

import { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { api } from '../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('userData'));

      if (token && userData) {
        try {
          // Validate token with backend
          await api.get('/auth/validate', {
            headers: { Authorization: `Bearer ${token}` },
          });

          setUser(userData);

          // redirect if on auth routes
          const isAuthRoute = ['/login', '/signup', '/forgot-password'].includes(location.pathname);
          if (isAuthRoute) {
            if (userData.role === 'SUPER_ADMIN') {
              navigate('/super-dashboard', { replace: true });
            } else if (userData.storeIds && userData.storeIds.length > 0) {
              navigate(`/stores/${userData.storeIds[0]}/dashboard`, { replace: true });
            }
          }
        } catch (err) {
          console.error('Token validation failed:', err);
          localStorage.removeItem('token');
          localStorage.removeItem('userData');
          setUser(null);
          if (location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
        }
      } else {
        setUser(null);
        if (location.pathname !== '/login' && location.pathname !== '/signup') {
          navigate('/login', { replace: true });
        }
      }
      setLoading(false);
    };
    initializeAuth();
  }, [location.pathname, navigate]);

  const login = async (email, password) => {
    try {
      setLoading(true);
      const res = await api.post('/auth/login', { email, password });
      if (res.data.success) {
        const token = res.data.data.token;
        localStorage.setItem('token', token);

        // 🔥 FIX: take storeIds and other info from response, not only decoded token
        const decoded = jwtDecode(token);
        const userData = {
          id: res.data.data.userId,
          name: res.data.data.name,
          email: res.data.data.email,
          role: res.data.data.role,
          storeIds: res.data.data.storeIds || [],
        };

        localStorage.setItem('userData', JSON.stringify(userData));
        setUser(userData);

        if (userData.role === 'SUPER_ADMIN') {
          navigate('/super-dashboard', { replace: true });
        } else if (userData.storeIds && userData.storeIds.length > 0) {
          navigate(`/stores/${userData.storeIds[0]}/dashboard`, { replace: true });
        } else {
          throw new Error('Store ID missing for non-super-admin user');
        }
        return { success: true };
      }
      return { success: false, error: res.data.message || 'Login failed' };
    } catch (err) {
      console.error('Login error:', err);
      return { success: false, error: err.response?.data?.message || err.message || 'Failed to login' };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setUser(null);
    navigate('/login', { replace: true });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
